import { computed, type ComputedRef } from 'vue';

export interface RewardsUserInfo {
  isLoggedIn?: boolean;
  isMember?: boolean;
  dividendBalance?: number | string | null;
  [key: string]: unknown;
}

const MIN_DISPLAYABLE_BALANCE = 0.01;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/** Normalizes the session balance to a finite number, treating missing or invalid values as zero. */
export const parseRewardsBalance = (value?: number | string | null): number => {
  const parsed = Number(value ?? 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

/** Formats a balance as US currency, for example 1234.5 becomes $1,234.50. */
export const formatRewardsBalance = (value: number): string => currencyFormatter.format(value);

export interface UseRewardsBalanceReturn {
  balance: ComputedRef<number>;
  formattedBalance: ComputedRef<string>;
  hasRewards: ComputedRef<boolean>;
}

/** Resolves the member's available rewards balance from the session user data. */
export default function useRewardsBalance(
  getUserInfo: () => RewardsUserInfo | null | undefined,
): UseRewardsBalanceReturn {
  const balance = computed<number>(() => parseRewardsBalance(getUserInfo()?.dividendBalance));

  const formattedBalance = computed<string>(() => formatRewardsBalance(balance.value));

  const hasRewards = computed<boolean>(() => {
    const userInfo = getUserInfo();

    if (!userInfo?.isLoggedIn || !userInfo?.isMember) return false;

    return balance.value >= MIN_DISPLAYABLE_BALANCE;
  });

  return { balance, formattedBalance, hasRewards };
}
